/* JapanSolved™ OUTBOUND Parent v1.7
   Hard Theme Sync + toggle-direction correction + safe reveal + hero slider
   ----------------------------------------------------------
   This script gives the OUTBOUND section its own internal
   theme-state authority while still obeying the global
   Shopify/header theme toggle.
*/
(function () {
  const root = document.querySelector('[data-js-ob-root]');
  if (!root) return;

  root.classList.add('js-ob-js-ready');

  /*
    Japanese Client Corridor marker:
    Pages using the OUTBOUND native Japanese template mark the
    visitor as Japanese/outbound-oriented. This does not replace
    Shopify localization, but it lets future header/footer snippets,
    legal-link rails, or form routing read the same preference.
  */
  try {
    if (window.localStorage) {
      window.localStorage.setItem('japansolved_language_preference', 'ja');
      window.localStorage.setItem('japansolved_client_corridor', 'outbound');
    }
    document.documentElement.setAttribute('data-js-market-language', 'ja');
    document.documentElement.setAttribute('data-js-client-corridor', 'outbound');
    root.setAttribute('data-js-market-language', 'ja');
    root.setAttribute('data-js-client-corridor', 'outbound');
  } catch (error) {
    /* Storage can be blocked. The visible page still works normally. */
  }

  if (!root.hasAttribute('data-js-ob-asset-status')) {
    root.setAttribute('data-js-ob-asset-status', 'placeholder');
  }

  const mode = root.getAttribute('data-theme-mode') || 'auto';
  const lightMQ = window.matchMedia('(prefers-color-scheme: light)');

  const THEME_KEYS = [
    'theme',
    'color-theme',
    'color_scheme',
    'color-scheme',
    'shopify-color-scheme',
    'preferred-theme',
    'prefers-color-scheme'
  ];

  function textLooksLikeLightOrDark(text) {
    if (!text) return null;

    const normalized = String(text).trim().toLowerCase();

    if (
      normalized === 'light' ||
      normalized.includes(' light ') ||
      normalized.includes('ライト') ||
      normalized.includes('明るい')
    ) {
      return 'light';
    }

    if (
      normalized === 'dark' ||
      normalized.includes(' dark ') ||
      normalized.includes('ダーク') ||
      normalized.includes('暗い')
    ) {
      return 'dark';
    }

    return null;
  }

  function attributeLooksLikeLightOrDark(value) {
    if (!value) return null;

    const normalized = String(value).toLowerCase();

    if (
      normalized.includes('light') ||
      normalized.includes('scheme-light') ||
      normalized.includes('theme-light')
    ) {
      return 'light';
    }

    if (
      normalized.includes('dark') ||
      normalized.includes('scheme-dark') ||
      normalized.includes('theme-dark')
    ) {
      return 'dark';
    }

    return null;
  }

  function readStoredTheme() {
    try {
      for (const key of THEME_KEYS) {
        const localValue = window.localStorage ? window.localStorage.getItem(key) : null;
        const sessionValue = window.sessionStorage ? window.sessionStorage.getItem(key) : null;

        const localTheme = attributeLooksLikeLightOrDark(localValue);
        if (localTheme) return localTheme;

        const sessionTheme = attributeLooksLikeLightOrDark(sessionValue);
        if (sessionTheme) return sessionTheme;
      }
    } catch (error) {
      /* Storage can be blocked. Ignore safely. */
    }

    return null;
  }

  function readDomTheme() {
    const html = document.documentElement;
    const body = document.body;

    const domTokens = [
      html.className,
      body ? body.className : '',
      html.getAttribute('data-theme'),
      body ? body.getAttribute('data-theme') : '',
      html.getAttribute('data-color-scheme'),
      body ? body.getAttribute('data-color-scheme') : '',
      html.getAttribute('color-scheme'),
      body ? body.getAttribute('color-scheme') : ''
    ];

    for (const token of domTokens) {
      const result = attributeLooksLikeLightOrDark(token);
      if (result) return result;
    }

    return null;
  }

  function readToggleTheme() {
    const selectors = [
      '[aria-label*="theme" i]',
      '[aria-label*="dark" i]',
      '[aria-label*="light" i]',
      '[data-theme-toggle]',
      '[data-color-scheme-toggle]',
      '.theme-toggle',
      '.color-scheme-toggle',
      'button'
    ];

    const candidates = Array.from(document.querySelectorAll(selectors.join(',')));

    for (const el of candidates) {
      const combined = [
        el.getAttribute('aria-label'),
        el.getAttribute('title'),
        el.getAttribute('data-theme'),
        el.getAttribute('data-current-theme'),
        el.getAttribute('data-color-scheme'),
        el.textContent
      ].filter(Boolean).join(' ');

      const visibleState =
        textLooksLikeLightOrDark(combined) ||
        attributeLooksLikeLightOrDark(combined);

      if (!visibleState) continue;

      /*
        JapanSolved header behavior:
        The visible pill label appears to describe the NEXT available mode,
        not the current active mode.

        Visible "DARK"  = click to switch to dark  = current page is light.
        Visible "LIGHT" = click to switch to light = current page is dark.

        Therefore we invert the visible button state.
      */
      return visibleState === 'dark' ? 'light' : 'dark';
    }

    return null;
  }

  function detectTheme() {
    if (mode === 'light') return 'light';
    if (mode === 'dark') return 'dark';

    /*
      Priority order:
      1. Explicit DOM classes/attributes from the Shopify theme.
      2. Stored theme preference.
      3. Header toggle visible state.
      4. OS preference as a final fallback.
    */
    return (
      readDomTheme() ||
      readStoredTheme() ||
      readToggleTheme() ||
      (lightMQ.matches ? 'light' : 'dark')
    );
  }

  function applyTheme(theme) {
    const normalized = theme === 'light' ? 'light' : 'dark';

    root.setAttribute('data-js-ob-current-theme', normalized);
    root.classList.toggle('js-ob-theme-light', normalized === 'light');
    root.classList.toggle('js-ob-theme-dark', normalized === 'dark');
  }

  function syncTheme() {
    applyTheme(detectTheme());
  }

  function delayedSync() {
    syncTheme();

    /*
      Shopify often updates theme classes after the click event.
      These delayed passes make the OUTBOUND section follow the final
      theme state rather than the pre-click state.
    */
    window.setTimeout(syncTheme, 60);
    window.setTimeout(syncTheme, 180);
    window.setTimeout(syncTheme, 420);
  }

  syncTheme();

  if (mode === 'auto') {
    const observeThemeTarget = (target) => {
      if (!target) return;

      const observer = new MutationObserver(delayedSync);

      observer.observe(target, {
        attributes: true,
        attributeFilter: [
          'class',
          'style',
          'data-theme',
          'data-color-scheme',
          'color-scheme'
        ]
      });
    };

    observeThemeTarget(document.documentElement);
    observeThemeTarget(document.body);

    if (typeof lightMQ.addEventListener === 'function') {
      lightMQ.addEventListener('change', delayedSync);
    } else if (typeof lightMQ.addListener === 'function') {
      lightMQ.addListener(delayedSync);
    }

    document.addEventListener(
      'click',
      function (event) {
        const target = event.target;
        if (!target || !target.closest) return;

        const clickedToggle = target.closest(
          '[data-theme-toggle], [data-color-scheme-toggle], .theme-toggle, .color-scheme-toggle, button, a'
        );

        if (!clickedToggle) return;

        const signature = [
          clickedToggle.getAttribute('aria-label'),
          clickedToggle.getAttribute('title'),
          clickedToggle.getAttribute('class'),
          clickedToggle.getAttribute('data-theme'),
          clickedToggle.getAttribute('data-color-scheme'),
          clickedToggle.textContent
        ].filter(Boolean).join(' ').toLowerCase();

        if (
          signature.includes('theme') ||
          signature.includes('dark') ||
          signature.includes('light') ||
          signature.includes('ダーク') ||
          signature.includes('ライト')
        ) {
          delayedSync();
        }
      },
      true
    );

    window.addEventListener('storage', delayedSync);
    window.addEventListener('pageshow', delayedSync);
  }

  const progress = root.querySelector('.js-ob-progress');

  const onScroll = () => {
    if (!progress) return;

    const max = Math.max(
      1,
      document.documentElement.scrollHeight - window.innerHeight
    );

    progress.style.transform = `scaleX(${Math.min(
      1,
      Math.max(0, window.scrollY / max)
    )})`;
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /*
    Reveal fix:
    Only explicitly marked elements animate.
    Cards and content remain visible by default.
  */
  const revealEls = Array.from(root.querySelectorAll('[data-js-ob-reveal]'));
  const showAll = () => revealEls.forEach((el) => el.classList.add('is-visible'));

  if ('IntersectionObserver' in window && revealEls.length) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.10,
        rootMargin: '0px 0px -4% 0px'
      }
    );

    revealEls.forEach((el, i) => {
      el.style.transitionDelay = `${Math.min(i * 36, 260)}ms`;
      io.observe(el);
    });

    window.setTimeout(showAll, 1800);
  } else {
    showAll();
  }

  const copySlides = Array.from(root.querySelectorAll('[data-js-ob-hero-copy]'));
  const visuals = Array.from(root.querySelectorAll('[data-js-ob-hero-visual]'));
  const dots = Array.from(root.querySelectorAll('[data-js-ob-hero-dot]'));

  let active = 0;
  let timer = null;

  function setSlide(i) {
    if (!copySlides.length) return;

    active = (i + copySlides.length) % copySlides.length;

    copySlides.forEach((el, idx) => {
      el.classList.toggle('is-active', idx === active);
    });

    visuals.forEach((el, idx) => {
      el.classList.toggle('is-active', idx === active);
    });

    dots.forEach((el, idx) => {
      el.classList.toggle('is-active', idx === active);
    });
  }

  function stop() {
    if (timer) window.clearInterval(timer);
    timer = null;
  }

  function start() {
    stop();
    timer = window.setInterval(() => setSlide(active + 1), 6500);
  }

  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      setSlide(parseInt(dot.getAttribute('data-js-ob-hero-dot') || '0', 10));
      start();
    });
  });

  if (
    copySlides.length > 1 &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    start();
  }
})();
